import React, { forwardRef } from 'react';
import { isNil, has } from 'lodash';
import cn from 'classnames';
// eslint-disable-next-line no-unused-vars
import { DateRangePicker, DateRangePickerProps } from '@progress/kendo-react-dateinputs';
import { format } from 'date-fns';
import BaseDateInput from './BaseDateInput';

const getReadOnlyDate = (date) => (date ? format(date, 'MM/DD/YYYY') : '');

const ReadOnlyDateRangePicker = ({ value, ...props }) => {
  const className = cn('read-only', { empty: isNil(value) });
  const readOnlyValue = `${getReadOnlyDate(value?.start)} - ${getReadOnlyDate(value?.end)}`;
  return <div className={className} {...props}>{readOnlyValue}</div>;
};

/**
 * @typedef {React.PropsWithoutRef<DateRangePickerProps>} TelerikDateRangePickerProps
 *
 * @typedef {Object} AdditionalDateRangePickerProps
 * @property {string} [dataTestId] Data Test ID for auto tests.
 * @property {boolean} [readOnly={false}] Indicates if the picker is read-only. False by default.
 * @property {boolean} [allowEmpty={true}] Indicates if value can be deleted or reset. True by default.
 *
 * @param {TelerikDateRangePickerProps & AdditionalDateRangePickerProps} props
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement}
 */
const BaseDateRangePicker = ({
  value, defaultValue, readOnly = false, onChange, dataTestId, allowEmpty = true, ...props
}, ref) => {
  const dateObj = value ?? defaultValue;

  const handleChange = (e) => {
    if (!onChange) {
      return;
    }

    const selected = e?.value;
    if (selected) {
      // Swap dated if start is later
      if (selected.start && selected.end && selected.start > selected.end) {
        const endDate = selected.end;
        selected.end = selected.start;
        selected.start = endDate;
      }

      // Set time to include the whole days
      selected.start?.setHours(0, 0, 0, 0);
      selected.end?.setHours(23, 59, 59, 999);
    }

    // Update event with a new value
    const newEvent = { ...e, value: selected };
    if (has(newEvent.target, 'value')) {
      newEvent.target.value = selected;
    }

    onChange(newEvent);
  };

  return (
    <span date-testid={dataTestId}>
      {readOnly ? <ReadOnlyDateRangePicker value={dateObj} /> : (
        <DateRangePicker
          ref={ref}
          value={value}
          startDateInput={(props) => <BaseDateInput {...props} clearButton={allowEmpty} />}
          endDateInput={(props) => <BaseDateInput {...props} clearButton={allowEmpty} />}
          onChange={handleChange}
          {...props}
        />
      )}
    </span>
  );
};

/**
 * A base date range picker component.
 *
 * @param {TelerikDateRangePickerProps & AdditionalDateRangePickerProps} props - Component props. Extends Telerik DateRangePicker props.
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement} Base date range picker component.
 */
const ForwardDateRangePicker = forwardRef(BaseDateRangePicker);

export default ForwardDateRangePicker;
